"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

interface HeroSliderSectionProps {
  primaryAction?: { label: string; href: string };
  secondaryAction?: { label: string; href: string };
  slides: Array<{
    title: string;
    subtitle: string;
    desc: string;
    imgUrl?: string;
    videoUrl?: string;
  }>;
}

const HeroSliderSection = ({ slides, primaryAction, secondaryAction }: HeroSliderSectionProps) => {
  const [bgImageLoaded, setBgImageLoaded] = useState(true);

  if (!slides || slides.length === 0) return null;

  // Extract YouTube video ID from URL
  const getYouTubeId = (url: string) => {
    if (!url) return null;
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  return (
    <section className="relative flex items-center justify-center w-full overflow-hidden h-[34rem] sm:h-[42rem] md:h-[52rem] lg:h-[clamp(60rem,86svh,80rem)]">
      {/* Loader */}
      {!bgImageLoaded && (
        <div className="absolute inset-0 z-[100] flex items-center justify-center bg-tertiary">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center gap-[1rem]"
          >
            <motion.div
              className="w-[4rem] h-[4rem] border-[0.25rem] border-white border-t-transparent rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </motion.div>
        </div>
      )}

      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        navigation={{
          nextEl: ".swiper-button-next-custom",
          prevEl: ".swiper-button-prev-custom",
        }}
        pagination={{
          clickable: true,
          dynamicBullets: true,
        }}
        autoplay={{
          delay: 500000,
          disableOnInteraction: false,
        }}
        loop={slides.length > 1}
        className="w-full h-full hero-swiper"
      >
        {slides.map((slide, index) => {
          const videoId = slide.videoUrl ? getYouTubeId(slide.videoUrl) : null;
          const isVideo = !!videoId;

          return (
            <SwiperSlide key={index}>
              <div className="relative w-full h-full">
                {/* Background Image or Video */}
                <div className="absolute inset-0 z-0 w-full h-full youtube-cover">
                  {isVideo ? (
                    <div className="relative w-full h-full overflow-hidden">
                      <iframe
                        src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&iv_load_policy=3&cc_load_policy=0&fs=0&disablekb=1&start=0&end=0&origin=${
                          typeof window !== "undefined"
                            ? window.location.origin
                            : ""
                        }`}
                        title={slide.title}
                        allow="autoplay; encrypted-media"
                        allowFullScreen={false}
                        className={`absolute youtube-embed ${
                          slide.title || slide.subtitle || slide.desc
                            ? "opacity-40"
                            : "opacity-100"
                        }`}
                        style={{
                          border: "none",
                          pointerEvents: "none",
                          objectFit: "cover",
                        }}
                      />
                      {/* Cover YouTube UI elements (logo, controls, etc.) */}
                      <div className="absolute inset-0 z-[2] pointer-events-none">
                        {/* Cover top-right corner where YouTube logo appears */}
                      </div>
                      {/* Bottom Gradient */}
                      <div className="absolute bottom-0 left-0 right-0 h-[40%] bg-gradient-to-t from-tertiary to-transparent z-[1]" />
                    </div>
                  ) : (
                    <>
                      <Image
                        src={slide.imgUrl || ""}
                        alt={slide.title}
                        priority={index === 0}
                        fill
                        className="object-cover object-center"
                        sizes="100vw"
                        onLoad={() => setBgImageLoaded(true)}
                      />
                      {/* Bottom Gradient */}
                      <div className="absolute bottom-0 left-0 right-0 h-[100%] bg-gradient-to-t from-primary/95 via-primary/45 to-primary/20 z-[1]" />
                    </>
                  )}
                </div>

                {/* Secondary Color Background Overlay */}
                <div className="absolute inset-0 z-[5] w-full h-full bg-primary/10" />

                {/* Content */}
                <div className="relative z-10 mx-auto px-[1.5rem] md:px-[3rem] py-[3rem] h-full flex items-center justify-center">
                  <div className="mx-auto max-w-[62rem] text-center w-full">
                    {/* Main Title */}
                    <motion.h1
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.45, delay: 0.04 }}
                      className="text-[2.25rem] sm:text-[3rem] md:text-[4rem] lg:text-[4.75rem] font-bold text-white mb-[1.5rem] md:mb-[2rem] leading-[1.35] font-sans [text-shadow:0_2px_18px_rgba(0,0,0,0.2)]"
                    >
                      {slide.title}
                    </motion.h1>

                    {/* Subtitle */}
                    <motion.p
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.45, delay: 0.08 }}
                      className="text-[1.5rem] sm:text-[1.8rem] md:text-[2.25rem] font-bold text-secondary max-w-[50rem] mx-auto mb-[1.5rem] px-[1rem]"
                    >
                      {slide.subtitle}
                    </motion.p>

                    {slide.desc && (
                      <>
                        {/* Description */}
                        <motion.p
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.45, delay: 0.12 }}
                          className="text-[0.9rem] sm:text-[1rem] md:text-[1.3rem] text-white max-w-[60rem] mx-auto px-[1rem]"
                        >
                          {slide.desc}
                        </motion.p>
                      </>
                    )}
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.45, delay: 0.12 }}
                      className="flex flex-wrap items-center justify-center gap-[1rem] mt-[2.5rem] md:mt-[3rem]"
                    >
                      {primaryAction?.href && <Link
                        href={primaryAction.href}
                        className="min-w-[10rem] text-[1.1rem] md:text-[1.2rem] font-bold bg-secondary text-primary rounded-[0.5rem] px-[2rem] py-[1rem] hover:bg-secondary/90 transition-colors"
                      >
                        {primaryAction.label}
                      </Link>}
                      {secondaryAction?.href && <Link
                        href={secondaryAction.href}
                        className="min-w-[10rem] text-[1.1rem] md:text-[1.2rem] font-bold text-white border border-white/60 bg-white/10 rounded-[0.5rem] px-[2rem] py-[1rem] hover:bg-white/20 transition-colors"
                      >
                        {secondaryAction.label}
                      </Link>}
                    </motion.div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

      {/* Custom Navigation Buttons */}
      {slides.length > 1 && (
        <>
          <button className="swiper-button-prev-custom absolute left-[0.5rem] md:left-[1rem] top-1/2 -translate-y-1/2 z-20 w-[2.5rem] h-[2.5rem] md:w-[3rem] md:h-[3rem] flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors">
            <ChevronLeft className="text-white text-[1.5rem] md:text-[2rem]" />
          </button>
          <button className="swiper-button-next-custom absolute right-[0.5rem] md:right-[1rem] top-1/2 -translate-y-1/2 z-20 w-[2.5rem] h-[2.5rem] md:w-[3rem] md:h-[3rem] flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors">
            <ChevronRight className="text-white text-[1.5rem] md:text-[2rem]" />
          </button>
        </>
      )}

      {/* Custom Pagination Styles */}
      <style jsx global>{`
        .hero-swiper .swiper-pagination {
          bottom: 1.5rem !important;
          left: 50% !important;
          transform: translateX(-50%) !important;
          width: auto !important;
        }
        .hero-swiper .swiper-pagination-bullet {
          width: 0.75rem !important;
          height: 0.75rem !important;
          background: rgba(255, 255, 255, 0.5) !important;
          opacity: 1 !important;
          margin: 0 0.25rem !important;
        }
        .hero-swiper .swiper-pagination-bullet-active {
          background: white !important;
          width: 1.5rem !important;
          border-radius: 0.375rem !important;
        }
        /* Hide all YouTube UI elements */
        iframe[src*="youtube.com"],
        iframe[src*="youtube-nocookie.com"],
        iframe.youtube-embed {
          pointer-events: none !important;
        }

        /* Hide YouTube watermark and UI overlays */
        iframe[src*="youtube.com"]::after,
        iframe[src*="youtube-nocookie.com"]::after {
          content: "" !important;
          display: none !important;
        }

        /* Additional wrapper to hide any YouTube UI */
        .youtube-embed {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
          border: none !important;
          pointer-events: none !important;
        }

        /* Force the YouTube player to cover the slider height */
        .youtube-cover iframe.youtube-embed {
          top: 50% !important;
          left: 50% !important;
          width: 177.78% !important; /* 16:9 scaled to fill height */
          height: 100% !important;
          transform: translate(-50%, -50%) !important;
        }
      `}</style>
    </section>
  );
};

export default HeroSliderSection;
